(()=>{
  'use strict';
  const INTERNAL = {
    summary: 'shipping review cart summary',
    automation: 'shipping review automation data',
    token: 'shipping review cart token'
  };
  let cartCache=null;
  let cartPromise=null;

  function allRoots(){
    const roots=[document], seen=new Set();
    for(let i=0;i<roots.length;i++){
      const root=roots[i];
      if(seen.has(root)) continue;
      seen.add(root);
      try{
        root.querySelectorAll('*').forEach(el=>{if(el.shadowRoot) roots.push(el.shadowRoot)});
      }catch(_e){}
    }
    return [...seen];
  }
  function qAll(sel){
    return allRoots().flatMap(root=>{try{return [...root.querySelectorAll(sel)]}catch(_e){return[]}});
  }
  function norm(value){
    return String(value||'').replace(/\s+/g,' ').trim().toLowerCase();
  }
  function descriptor(field){
    const labels=[];
    try{if(field.labels) labels.push(...[...field.labels].map(x=>x.textContent||''))}catch(_e){}
    const id=field.id;
    if(id){
      try{labels.push(...qAll(`label[for="${CSS.escape(id)}"]`).map(x=>x.textContent||''))}catch(_e){}
    }
    const parent=field.closest?.('label,.form-field,.field,[class*="field"],div');
    return norm([field.name,field.id,field.placeholder,field.getAttribute?.('aria-label'),field.getAttribute?.('data-label'),...labels,parent?.textContent].filter(Boolean).join(' '));
  }
  function fieldsFor(needle){
    return qAll('textarea,input:not([type="submit"]),select').filter(f=>descriptor(f).includes(needle));
  }
  function isShippingReviewForm(form){
    const fields=[...form.querySelectorAll('textarea,input,select')];
    return fields.some(f=>{
      const d=descriptor(f);
      return d.includes(INTERNAL.automation)||d.includes(INTERNAL.summary)||d.includes(INTERNAL.token);
    });
  }
  function centerShippingReviewForm(form){
    if(!form) return;
    const applyCenter=(node)=>{
      if(!node||!node.style) return;
      try{
        node.setAttribute?.('data-unicus-shipping-review-centered','1');
        node.style.setProperty('width','100%','important');
        node.style.setProperty('max-width','640px','important');
        node.style.setProperty('margin-left','auto','important');
        node.style.setProperty('margin-right','auto','important');
        node.style.setProperty('box-sizing','border-box','important');
      }catch(_e){}
    };

    // Center the actual Shopify Form without changing any other Forms block.
    applyCenter(form);

    // Shopify Forms can render inside a shadow-root host. Center that host too so
    // its title/content and fields move together as one unit on desktop.
    try{
      const root=form.getRootNode?.();
      const host=root&&root.host?root.host:null;
      if(host){
        applyCenter(host);
        host.style?.setProperty('display','block','important');
        const appBlock=host.closest?.('[data-shopify-app-block],.shopify-block');
        if(appBlock) applyCenter(appBlock);
      }else{
        const appBlock=form.closest?.('[data-shopify-app-block],.shopify-block');
        if(appBlock) applyCenter(appBlock);
      }
    }catch(_e){}
  }
  function hideNode(node){
    if(!node) return;
    try{
      node.hidden=true;
      node.setAttribute?.('aria-hidden','true');
      node.setAttribute?.('data-unicus-shipping-review-internal','1');
      node.style?.setProperty('display','none','important');
    }catch(_e){}
  }
  function visibleControlCount(node){
    try{return node.querySelectorAll('textarea,select,input:not([type="hidden"]),button').length}catch(_e){return 999}
  }
  function hideInternalField(field,needle){
    if(!field) return;

    // Always hide the actual control, even if Shopify changes wrapper markup.
    hideNode(field);

    // Hide explicitly associated labels.
    try{if(field.labels) [...field.labels].forEach(hideNode)}catch(_e){}
    if(field.id){
      try{qAll(`label[for="${CSS.escape(field.id)}"]`).forEach(hideNode)}catch(_e){}
    }
    try{
      const labelledBy=(field.getAttribute?.('aria-labelledby')||'').trim();
      if(labelledBy){
        labelledBy.split(/\s+/).filter(Boolean).forEach(id=>qAll(`#${CSS.escape(id)}`).forEach(hideNode));
      }
    }catch(_e){}

    // Shopify Forms may wrap a local field in several nested DIVs. Hide the
    // highest small wrapper that still belongs only to this one field, rather
    // than the nearest inner DIV used by the previous bridge.
    let node=field.parentElement;
    let best=null;
    for(let depth=0;node&&depth<10;depth++,node=node.parentElement){
      if(node.matches?.('form')) break;
      const text=norm(node.textContent);
      const controls=visibleControlCount(node);
      if(text.includes(needle)&&controls<=1){best=node;continue}
      if(best&&controls>1) break;
    }
    if(best) hideNode(best);
  }
  async function getCart(){
    if(cartCache) return cartCache;
    if(cartPromise) return cartPromise;
    cartPromise=fetch('/cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}})
      .then(r=>{if(!r.ok) throw new Error(`cart.js ${r.status}`);return r.json()})
      .then(c=>{cartCache=c;return c})
      .catch(err=>{console.warn('[ShippingReview] Unable to load cart',err);return null})
      .finally(()=>{cartPromise=null});
    return cartPromise;
  }
  function gidVariant(id){
    const s=String(id||'').trim();
    if(!s) return '';
    return s.startsWith('gid://shopify/ProductVariant/')?s:`gid://shopify/ProductVariant/${s}`;
  }
  function payloadFromCart(cart){
    const items=(cart?.items||[]).map(item=>({
      variantId:gidVariant(item.id),
      quantity:Number(item.quantity||0),
      sku:item.sku||'',
      productTitle:item.product_title||item.title||'',
      variantTitle:item.variant_title||'',
      productId:item.product_id?`gid://shopify/Product/${item.product_id}`:'',
      unitPriceCents:Number(item.final_price??item.price??0),
      linePriceCents:Number(item.final_line_price??item.line_price??0)
    })).filter(x=>x.variantId&&Number.isInteger(x.quantity)&&x.quantity>0);
    return JSON.stringify({
      schema:'unicus-shipping-review-v1',
      cartToken:String(cart?.token||''),
      currency:String(cart?.currency||''),
      itemCount:items.length,
      totalQuantity:items.reduce((a,x)=>a+x.quantity,0),
      productSubtotalCents:Number(cart?.items_subtotal_price??cart?.total_price??0),
      items
    });
  }
  function summaryFromCart(cart){
    const lines=['Shipping Review Cart Summary',`Cart token: ${cart?.token||''}`,`Product subtotal cents: ${cart?.items_subtotal_price??cart?.total_price??0}`,'','Items:'];
    (cart?.items||[]).forEach(item=>{
      lines.push(`- ${item.product_title||item.title||''}${item.variant_title&&item.variant_title!=='Default Title'?` / ${item.variant_title}`:''}`);
      lines.push(`  SKU: ${item.sku||''}`);
      lines.push(`  Quantity: ${Number(item.quantity||0)}`);
      lines.push(`  Variant ID: ${gidVariant(item.id)}`);
      lines.push(`  Unit price cents: ${Number(item.final_price??item.price??0)}`);
    });
    return lines.join('\n');
  }
  function setField(field,value){
    if(!field) return;
    try{
      field.value=value;
      field.dispatchEvent(new Event('input',{bubbles:true,composed:true}));
      field.dispatchEvent(new Event('change',{bubbles:true,composed:true}));
    }catch(_e){}
  }
  async function sync(){
    const cart=await getCart();
    if(!cart||!(cart.items||[]).length) return false;
    const summary=summaryFromCart(cart), automation=payloadFromCart(cart), token=String(cart.token||'');
    const groups={
      summary:fieldsFor(INTERNAL.summary),
      automation:fieldsFor(INTERNAL.automation),
      token:fieldsFor(INTERNAL.token)
    };
    groups.summary.forEach(f=>hideInternalField(f,INTERNAL.summary));
    groups.automation.forEach(f=>hideInternalField(f,INTERNAL.automation));
    groups.token.forEach(f=>hideInternalField(f,INTERNAL.token));
    groups.summary.forEach(f=>setField(f,summary));
    groups.automation.forEach(f=>setField(f,automation));
    groups.token.forEach(f=>setField(f,token));

    let detected=false;
    qAll('form').forEach(form=>{
      if(!isShippingReviewForm(form)) return;
      detected=true;
      centerShippingReviewForm(form);
      if(form.dataset.shippingReviewBound!=='1'){
        form.dataset.shippingReviewBound='1';
        form.addEventListener('submit',()=>{sync()},true);
        form.addEventListener('formdata',ev=>{
          const s=fieldsFor(INTERNAL.summary), a=fieldsFor(INTERNAL.automation), t=fieldsFor(INTERNAL.token);
          s.forEach(f=>{if(f.name) ev.formData.set(f.name,summary)});
          a.forEach(f=>{if(f.name) ev.formData.set(f.name,automation)});
          t.forEach(f=>{if(f.name) ev.formData.set(f.name,token)});
        });
      }
    });
    qAll('[data-shipping-review-fallback]').forEach(el=>{el.hidden=detected});
    return detected;
  }
  function init(){
    sync();
    let ticks=0;
    const timer=setInterval(()=>{sync();if(++ticks>=120) clearInterval(timer)},500);
    try{new MutationObserver(()=>sync()).observe(document.body,{childList:true,subtree:true})}catch(_e){}
  }
  document.readyState==='loading'?document.addEventListener('DOMContentLoaded',init):init();
})();
